'use client';

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import type { RankStripItem } from '@/lib/product-rank-strip';
import { useBrowserLocation } from '@/lib/use-browser-location';

interface SeedState {
	items: RankStripItem[];
	collection: string;
	type: string;
}

interface RankStripContextValue {
	/** Stable candidate list for the current trade/theme browsing session. */
	seedItems: RankStripItem[];
	/** Collection handle the seed was built around (the grouping key). */
	seedCollection: string;
	/** Product type the seed was built around. */
	seedType: string;
	setSeedItems: (items: RankStripItem[], collection: string, type: string) => void;
	/** Persisted horizontal scroll offset so the rail keeps its position across PDP nav. */
	scrollPosition: { current: number };
}

const RankStripContext = createContext<RankStripContextValue | null>(null);
const EMPTY_SEED: SeedState = { items: [], collection: '', type: '' };

function isProductDetailPath(pathname: string) {
	return /\/products\/[^/]+\/?$/.test(pathname);
}

/**
 * Holds the stable rail seed for a PDP browsing session. Mounted in the shared
 * market layout, so the seed survives product -> product navigation and only
 * resets when the shopper leaves product detail pages (e.g. back to a
 * collection), at which point the next PDP repopulates it.
 */
export function RankStripProvider({ children }: { children: ReactNode }) {
	// useBrowserLocation, not usePathname: pathname only drives the reset
	// effect, and reading URL data during render would turn the layout's
	// Suspense boundary into a runtime hole on every route — no page content
	// in the prerendered shell.
	const { pathname } = useBrowserLocation();
	const [seed, setSeed] = useState<SeedState>(EMPTY_SEED);
	const scrollPosition = useRef(0);

	useEffect(() => {
		// Empty until the mount effect fires; never treat that as "left the PDP".
		if (!pathname || isProductDetailPath(pathname)) return;

		scrollPosition.current = 0;
		setSeed((current) =>
			current === EMPTY_SEED || current.items.length === 0 ? current : EMPTY_SEED,
		);
	}, [pathname]);

	const setSeedItems = useCallback((items: RankStripItem[], collection: string, type: string) => {
		setSeed({
			items,
			collection: collection.trim().toLowerCase(),
			type: type.trim().toLowerCase(),
		});
	}, []);

	return (
		<RankStripContext.Provider
			value={{
				seedItems: seed.items,
				seedCollection: seed.collection,
				seedType: seed.type,
				setSeedItems,
				scrollPosition,
			}}
		>
			{children}
		</RankStripContext.Provider>
	);
}

export function useRankStrip() {
	return useContext(RankStripContext);
}
